<script lang="ts" setup>
import Draggable from 'vuedraggable';
import type { Header } from '../helpers';

export interface Props
{
    headers: Header[];
}

const props = withDefaults(defineProps<Props>(), {});
const visible = defineModel<Record<string, boolean>>('visible');
const positions = defineModel<Record<string, number>>('positions');

const { $t } = useLocalization();
const dropdown = ref<HTMLElement>(null);
const show = ref(false);
const onToggle = (): void =>
{
    show.value = !show.value;
};

onClickOutside(dropdown, () =>
{
    show.value = false;
},
{ ignore: ['.modal'] });

const search = ref('');
const resetSearch = (): void =>
{
    search.value = '';
};
const canDrag = (): boolean =>
{
    return search.value.length == 0;
};

const columns = computed({
    get: () => props.headers
        .where(p =>
            (p.title || $t('[[[<Brak nazwy>]]]')).toLowerCase().includes(search.value.toLowerCase()) &&
            !['checkbox', 'hierarchy', 'handle', 'buttons'].includes(p.type) &&
            !p.disabled
        )
        .orderBy(p => intermediate.value.positions[p.key] ?? p.position)
        .toArray(),
    set: value => value
        .forEach((p, index) => intermediate.value.positions[p.key] = index)
});
const intermediate = ref({
    visible: [] as string[],
    positions: {} as Record<string, number>
});

const update = (): void =>
{
    visible.value = {
        ...props.headers.where(p => intermediate.value.visible.includes(p.key)).select(p => p.key).record(p => p, _ => true),
        ...props.headers.where(p => !intermediate.value.visible.includes(p.key)).select(p => p.key).record(p => p, _ => false)
    };
    positions.value = intermediate.value.positions;

    setColumnsState({
        visible: visible.value,
        positions: positions.value
    });
};
const reset = (): void =>
{
    visible.value = {};
    positions.value = {};

    setColumnsState({
        visible: visible.value,
        positions: positions.value
    });
};

watch(() => props.headers, () =>
{
    intermediate.value.visible = props.headers.where(p => p.visible).select(p => p.key).toArray();
    intermediate.value.positions = props.headers.record(p => p.key, p => p.position);
},
{ immediate: true });
</script>

<template>
    <Portal to="list-view-settings">
        <div ref="dropdown" class="d-flex dropdown">
            <button type="button" class="btn btn-secondary px-2" @click.stop="onToggle()">
                <i class="far fa-fw fa-table" />
            </button>
            <div class="dropdown-menu shadow p-0" :class="{ 'show': show }">
                <div class="p-2">
                    <IdeoInputGroup class="mb-2">
                        <IdeoFormInput v-model="search" type="text" name="search" :placeholder="$t('[[[Wyszukaj...]]]')" />
                        <template #append>
                            <IdeoButton icon="fas fa-fw fa-times" @click="resetSearch()" />
                        </template>
                    </IdeoInputGroup>
                    <div class="columns scroll pt-2">
                        <Draggable v-model="columns" item-key="key" tag="div" handle=".handle" :move="canDrag">
                            <template #item="{ element }">
                                <div class="d-flex">
                                    <i class="fa-regular fa-grip-dots-vertical p-1 handle" />
                                    <IdeoFormCheckbox v-model="intermediate.visible" :value="element.key">
                                        {{ element.title || $t('[[[<Brak nazwy>]]]') }}
                                    </IdeoFormCheckbox>
                                </div>
                            </template>
                        </Draggable>
                    </div>
                    <div v-if="columns.length == 0" class="d-flex justify-content-center py-3">
                        {{ $t('[[[Nie znaleziono kolumn.]]]') }}
                    </div>
                </div>
                <div class="text-end border-top p-2">
                    <IdeoButton variant="light" icon="far fa-fw fa-circle-check me-2" @click="reset()">
                        {{ $t('[[[Domyślne]]]') }}
                    </IdeoButton>
                    <IdeoButton variant="primary" icon="far fa-fw fa-check me-2" @click="update()">
                        {{ $t('[[[Potwierdź]]]') }}
                    </IdeoButton>
                </div>
            </div>
        </div>
    </Portal>
</template>

<style lang="scss" scoped>
.dropdown > .btn {
    min-height: 34px;
}
.dropdown-menu {
    min-width: var(--ideo-dropdown-width);
    top: 100%;
    right: 4px;

    > * {
        margin: -1px;
    }
}
.columns {
    max-height: 295px;
}
.handle {
    cursor: pointer;
}
</style>
